<template>
    <div class="error-message alert" :class="alertClass" role="alert">
        <div class="d-flex align-items-start">
            <div class="error-icon me-2">
                <i :class="iconClass"></i>
            </div>
            <div class="error-content flex-grow-1">
                <div class="error-title" v-if="errorTitle">
                    <strong>{{ errorTitle }}</strong>
                </div>
                <div class="error-text">{{ error }}</div>
                
                <!-- Rate limit specific information -->
                <div v-if="errorType === 'rate-limit'" class="rate-limit-info mt-2">
                    <small class="text-muted">
                        <i class="fas fa-clock"></i>
                        Please wait a moment before asking another question.
                        You can ask {{ rateLimitMax }} questions per minute.
                    </small>
                </div>
                
                <!-- Contact information for critical errors -->
                <div v-if="showContactInfo" class="contact-info mt-2">
                    <small class="text-muted">
                        If this problem persists, please 
                        <a href="#" @click.prevent="$emit('contact-support')" class="alert-link">
                            contact support
                        </a>
                        for assistance.
                    </small>
                </div>
                
                <!-- Helpful suggestions -->
                <div v-if="suggestions.length > 0" class="suggestions mt-2">
                    <small class="text-muted">
                        <strong>Try:</strong>
                        <ul class="mb-0 mt-1">
                            <li v-for="suggestion in suggestions" :key="suggestion">
                                {{ suggestion }}
                            </li>
                        </ul>
                    </small>
                </div>
            </div>
            
            <!-- Action buttons -->
            <div class="error-actions ms-2">
                <div class="btn-group-vertical btn-group-sm">
                    <button 
                        v-if="canRetry"
                        type="button"
                        class="btn btn-outline-primary btn-sm"
                        @click="$emit('retry')"
                        title="Try again"
                    >
                        <i class="fas fa-redo"></i>
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        @click="$emit('clear')"
                        title="Dismiss"
                    >
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ErrorMessage',
    
    props: {
        error: {
            type: String,
            required: true
        },
        errorType: {
            type: String,
            default: 'general'
        }
    },
    
    data() {
        return {
            rateLimitMax: 10 // This could be a prop if it varies
        }
    },
    
    computed: {
        alertClass() {
            const classMap = {
                'rate-limit': 'alert-warning',
                'validation': 'alert-danger',
                'network': 'alert-warning',
                'server': 'alert-danger',
                'not-found': 'alert-info',
                'general': 'alert-danger'
            }
            
            return classMap[this.errorType] || 'alert-danger'
        },
        
        iconClass() {
            const iconMap = {
                'rate-limit': 'fas fa-clock text-warning',
                'validation': 'fas fa-exclamation-triangle text-danger',
                'network': 'fas fa-wifi text-warning',
                'server': 'fas fa-server text-danger',
                'not-found': 'fas fa-info-circle text-info',
                'general': 'fas fa-exclamation-circle text-danger'
            }
            
            return iconMap[this.errorType] || 'fas fa-exclamation-circle text-danger'
        },
        
        errorTitle() {
            const titleMap = {
                'rate-limit': 'Rate Limit Reached',
                'validation': 'Invalid Input',
                'network': 'Connection Problem',
                'server': 'Server Error',
                'not-found': 'Event Not Found',
                'general': 'Error'
            }
            
            return titleMap[this.errorType] || null
        },
        
        canRetry() {
            return ['network', 'server', 'general'].indexOf(this.errorType) !== -1
        },
        
        showContactInfo() {
            return ['server', 'general'].indexOf(this.errorType) !== -1
        },
        
        suggestions() {
            const suggestionMap = {
                'validation': [
                    'Avoid using HTML tags or special characters',
                    'Be specific about what you want to know'
                ],
                'network': [
                    'Check your internet connection',
                    'Try refreshing the page',
                    'Wait a moment and try again'
                ],
                'rate-limit': [
                    'Wait about a minute before asking another question',
                    'Review previous answers to see if your question was already addressed'
                ],
                'not-found': [
                    'Make sure you\'re on the correct event page',
                    'Try refreshing the page',
                    'Contact the event organizer if the problem persists'
                ]
            }
            
            return suggestionMap[this.errorType] || []
        }
    }
}
</script>

<style scoped>
.error-message {
    border-left-width: 4px;
}

.error-icon {
    margin-top: 0.125rem;
    font-size: 1.1rem;
}

.error-content {
    /* Main error content styling */
}

.error-title {
    margin-bottom: 0.25rem;
}

.error-text {
    margin-bottom: 0;
}

.error-actions {
    /* Action buttons container */
}

.suggestions ul {
    padding-left: 1.25rem;
    font-size: 0.875rem;
}

.suggestions li {
    margin-bottom: 0.25rem;
}

.suggestions li:last-child {
    margin-bottom: 0;
}

.contact-info a {
    text-decoration: none;
}

.contact-info a:hover {
    text-decoration: underline;
}

.btn-group-vertical .btn {
    border-radius: 0.25rem;
    margin-bottom: 0.25rem;
}

.btn-group-vertical .btn:last-child {
    margin-bottom: 0;
}

/* Responsive adjustments */
@media (max-width: 576px) {
    .d-flex {
        flex-direction: column;
    }
    
    .error-actions {
        margin-top: 0.75rem;
        margin-left: 0 !important;
    }
    
    .btn-group-vertical {
        flex-direction: row;
    }
    
    .btn-group-vertical .btn {
        margin-bottom: 0;
        margin-right: 0.25rem;
    }
    
    .btn-group-vertical .btn:last-child {
        margin-right: 0;
    }
}

/* Accessibility improvements */
.alert {
    border-radius: 0.375rem;
}

.btn:focus {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Animation for error appearance */
.error-message {
    animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .error-message {
        animation: none;
    }
}
</style>


// WEBPACK FOOTER //
// src/Assets/components/AiAgents/FaqAgent/components/ErrorMessage.vue